import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { AppUpdate } from "@capawesome/capacitor-app-update";
import { api, ApiError } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { useAppUpdate } from "../../useAppUpdate";
import { getAppInfo, openPlayListing, requestPlayReview } from "../../appStore";

/* ---------------- update notice ---------------- */

// A dismissible banner when a newer version is on Google Play, and a
// full-screen "please update" when the server says this build is too old.
export function UpdateNotice() {
  const { t } = useLanguage();
  const { available, required } = useAppUpdate();
  const [dismissed, setDismissed] = useState(false);

  async function update() {
    if (required) {
      try {
        await AppUpdate.performImmediateUpdate();
        return;
      } catch {
        // fall back to the store page
      }
    }
    openPlayListing();
  }

  if (required) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "var(--app-bg)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28, textAlign: "center" }}>
        <img src="/logo.svg" alt="" width={84} height={84} />
        <div className="reg-title" style={{ marginTop: 18 }}>{t("appUpdate.requiredTitle")}</div>
        <div className="sub" style={{ margin: "8px 0 22px", maxWidth: 320 }}>{t("appUpdate.requiredBody")}</div>
        <button className="btn-solid" onClick={update}>{t("appUpdate.updateBtn")}</button>
      </div>
    );
  }
  if (!available || dismissed) return null;
  return (
    <div className="card" style={{ marginBottom: 14, borderColor: "var(--brand-cta)", display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b style={{ fontSize: 13 }}>{t("appUpdate.bannerTitle")}</b>
        <div className="sub" style={{ fontSize: 12 }}>{t("appUpdate.bannerBody")}</div>
      </div>
      <button className="btn-solid btn-sm" onClick={update}>{t("appUpdate.updateBtn")}</button>
      <button type="button" className="link-btn" aria-label={t("appUpdate.dismissAria")} onClick={() => setDismissed(true)}>✕</button>
    </div>
  );
}

/* ---------------- rating ---------------- */

function Stars({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 8, margin: "14px 0" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n}`}
          onClick={() => onChange(n)}
          style={{ background: "none", border: "none", fontSize: 40, lineHeight: 1, cursor: "pointer", color: n <= value ? "#fbbf24" : "var(--line)", padding: 2 }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

// Stars and an optional comment. Saved on our server for everyone; people who
// give 4 or 5 are then invited to leave the same rating on Google Play.
export function RateAppDialog({ onClose }: { onClose: (rated: boolean) => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit() {
    if (!rating) return;
    setBusy(true);
    try {
      const info = await getAppInfo();
      await api.post("/api/feedback", {
        rating,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(info ? { appVersion: info.version } : {}),
        platform: Capacitor.isNativePlatform() ? "android" : "web",
      });
      setDone(true);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="filter-backdrop" onClick={() => onClose(done)} />
      <div className="filter-sheet" style={{ textAlign: "center" }}>
        <div className="filter-sheet-handle" />
        {!done ? (
          <>
            <div className="reg-title" style={{ marginBottom: 4 }}>{t("rateApp.title")}</div>
            <div className="sub">{t("rateApp.subtitle")}</div>
            <Stars value={rating} onChange={setRating} />
            {rating > 0 && (
              <div className="field" style={{ textAlign: "left" }}>
                <label>{rating >= 4 ? t("rateApp.commentLikeLabel") : t("rateApp.commentFixLabel")}</label>
                <div className="input-wrap">
                  <textarea rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("rateApp.commentPlaceholder")} />
                </div>
              </div>
            )}
            <button className="btn-solid" disabled={!rating || busy} onClick={submit}>{busy ? t("common.saving") : t("rateApp.submitBtn")}</button>
            <div><button type="button" className="link-btn" style={{ marginTop: 10 }} onClick={() => onClose(false)}>{t("rateApp.notNow")}</button></div>
          </>
        ) : rating >= 4 ? (
          <>
            <div className="plan-success-icon">★</div>
            <div className="reg-title" style={{ marginBottom: 6 }}>{t("rateApp.thanksHappyTitle")}</div>
            <div className="sub" style={{ marginBottom: 18 }}>{t("rateApp.thanksHappyBody")}</div>
            <button className="btn-solid" onClick={async () => { await requestPlayReview(); onClose(true); }}>{t("rateApp.rateOnPlayBtn")}</button>
            <div><button type="button" className="link-btn" style={{ marginTop: 6 }} onClick={() => onClose(true)}>{t("rateApp.maybeLater")}</button></div>
          </>
        ) : (
          <>
            <div className="plan-success-icon">✓</div>
            <div className="reg-title" style={{ marginBottom: 6 }}>{t("rateApp.thanksSadTitle")}</div>
            <div className="sub" style={{ marginBottom: 18 }}>{t("rateApp.thanksSadBody")}</div>
            <button className="btn-solid" onClick={() => onClose(true)}>{t("rateApp.close")}</button>
          </>
        )}
      </div>
    </>
  );
}

// Settings row: "Rate ToLetBook"
export function RateAppMenuItem() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="menu-item" onClick={() => setOpen(true)} role="button">
        <div className="menu-icon blue">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2z" />
          </svg>
        </div>
        <span className="mi-label">{t("rateApp.menuLabel")}</span>
      </div>
      {open && <RateAppDialog onClose={() => setOpen(false)} />}
    </>
  );
}

// Asks once the person has really used the app: at least 5 launches over 3
// days, never again for 30 days after "Not now", never if they already rated.
const K_FIRST = "toletbook_first_seen";
const K_LAUNCHES = "toletbook_launch_count";
const K_DISMISSED = "toletbook_rate_dismissed_at";
const DAY = 86_400_000;

export function RatingPrompt() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    (async () => {
      try {
        const now = Date.now();
        const first = Number(localStorage.getItem(K_FIRST)) || now;
        localStorage.setItem(K_FIRST, String(first));
        const launches = (Number(localStorage.getItem(K_LAUNCHES)) || 0) + 1;
        localStorage.setItem(K_LAUNCHES, String(launches));
        const dismissed = Number(localStorage.getItem(K_DISMISSED)) || 0;
        if (launches < 5 || now - first < 3 * DAY) return;
        if (dismissed && now - dismissed < 30 * DAY) return;
        const mine = await api.get("/api/feedback/mine");
        if (mine?.rated || cancelled) return;
        timer = setTimeout(() => !cancelled && setShow(true), 8000);
      } catch {
        // storage blocked or offline: skip the prompt
      }
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  if (!show) return null;
  return (
    <RateAppDialog
      onClose={(rated) => {
        setShow(false);
        try { if (!rated) localStorage.setItem(K_DISMISSED, String(Date.now())); } catch { /* ignore */ }
      }}
    />
  );
}
